import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowRight, ChevronLeft, ChevronRight, Upload, Check, Mail, PenLine } from 'lucide-react';

const ScanOverlay = ({ t }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-[26px]">
    <div className="absolute left-0 right-0 h-0.5 bg-amber-400 shadow-[0_0_12px_2px_rgba(245,158,11,0.5)]" style={{ animation: 'scanLine 2.5s ease-in-out infinite' }} />
    <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-amber-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg" style={{ animation: 'fadeInOut 2.5s ease-in-out infinite' }}>
      <Upload className="w-3 h-3" />
      {t('landing.homepage.overlayScan')}
    </div>
  </div>
);

const SentOverlay = ({ t }) => (
  <div className="absolute -left-3 sm:-left-4 top-1/3 pointer-events-none" style={{ animation: 'slideUp 0.8s ease-out both', animationDelay: '0.3s' }}>
    <div className="bg-emerald-500/95 backdrop-blur-sm text-white px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-xl shadow-emerald-500/30 flex items-center gap-2">
      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 flex items-center justify-center"><Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></div>
      <div>
        <div className="text-xs sm:text-sm font-semibold leading-tight">{t('landing.homepage.overlaySent')}</div>
        <div className="text-[10px] sm:text-xs text-emerald-100">{t('landing.homepage.overlaySentEmail')}</div>
      </div>
      <Check className="w-4 h-4 sm:w-5 sm:h-5 ml-1 text-emerald-200" />
    </div>
  </div>
);

const TypingOverlay = ({ t }) => (
  <div className="absolute -left-3 sm:-left-4 top-16 sm:top-20 pointer-events-none" style={{ animation: 'slideUp 0.6s ease-out both', animationDelay: '0.2s' }}>
    <div className="bg-emerald-500/95 backdrop-blur-sm text-white px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-xl shadow-emerald-500/30 flex items-center gap-2">
      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 flex items-center justify-center"><PenLine className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></div>
      <div>
        <div className="text-xs sm:text-sm font-semibold leading-tight">{t('landing.homepage.overlayFilling')}</div>
        <div className="text-[10px] sm:text-xs text-emerald-100 flex items-center gap-1">
          {t('landing.homepage.overlayFillingCompany')}
          <span className="inline-block w-0.5 h-3 bg-white/80" style={{ animation: 'blink 1s step-end infinite' }} />
        </div>
      </div>
    </div>
  </div>
);

const PhoneMockup = ({ src, overlay, showOverlay, t }) => (
  <div className="relative flex-shrink-0 w-[140px] sm:w-[150px] md:w-[190px] lg:w-[220px]">
    <div className="relative rounded-[24px] sm:rounded-[28px] md:rounded-[32px] border-[4px] sm:border-[5px] md:border-[6px] border-slate-700/80 bg-slate-900 shadow-2xl shadow-black/60 ring-1 ring-white/10">
      <div className="overflow-hidden rounded-[20px] sm:rounded-[23px] md:rounded-[26px]">
        <img src={src} alt="SmartCMR App" className="w-full h-auto block" loading="eager" />
      </div>
      {showOverlay && overlay === 'scanning' && <ScanOverlay t={t} />}
    </div>
    {showOverlay && overlay === 'sent' && <SentOverlay t={t} />}
    {showOverlay && overlay === 'typing' && <TypingOverlay t={t} />}
  </div>
);

const LaptopMockup = ({ src }) => (
  <div className="relative flex-shrink-0 w-[320px] sm:w-[360px] md:w-[420px] lg:w-[500px]">
    <div className="relative rounded-t-xl border-[5px] border-b-0 border-slate-600/80 bg-slate-800 shadow-2xl shadow-black/60 overflow-hidden ring-1 ring-white/10">
      <img src={src} alt="SmartCMR Dashboard" className="w-full h-auto block" loading="eager" />
    </div>
    <div className="h-3.5 bg-gradient-to-b from-slate-500 to-slate-600 rounded-b-xl mx-[-2px] shadow-lg" />
    <div className="h-1.5 bg-slate-500/60 rounded-b-lg mx-8" />
  </div>
);

const SliderStyles = () => (
  <style>{`
    @keyframes scanLine { 0%, 100% { top: 15%; } 50% { top: 85%; } }
    @keyframes fadeInOut { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes blink { 50% { opacity: 0; } }
  `}</style>
);

const HomepageSlider = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [overlayReady, setOverlayReady] = useState(true);
  const timerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const isSwiping = useRef(false);

  const slides = [
    {
      id: 1,
      title: t('landing.homepage.slide1Title'),
      subtitle: t('landing.homepage.slide1Sub'),
      cta: t('landing.homepage.slide1Cta'),
      ctaLink: '/register',
      subCta: t('landing.homepage.slide1SubCta'),
      eyebrow: t('landing.homepage.slide1Eyebrow'),
      mockup: '/images/mockup-cmr-fields.png',
      mockupType: 'phone',
      accent: '#3B82F6',
      overlay: null,
    },
    {
      id: 2,
      title: t('landing.homepage.slide2Title'),
      subtitle: t('landing.homepage.slide2Sub'),
      cta: t('landing.homepage.slide2Cta'),
      ctaLink: '/register',
      mockup: '/images/mockup-cmr-filling.png',
      mockupType: 'phone',
      accent: '#10B981',
      overlay: 'typing',
    },
    {
      id: 3,
      title: t('landing.homepage.slide3Title'),
      subtitle: t('landing.homepage.slide3Sub'),
      cta: t('landing.homepage.slide3Cta'),
      ctaLink: '#how-it-works',
      ctaIsAnchor: true,
      mockup: '/images/mockup-cmr-upload.png',
      mockupType: 'phone',
      accent: '#F59E0B',
      overlay: 'scanning',
    },
    {
      id: 4,
      title: t('landing.homepage.slide4Title'),
      subtitle: t('landing.homepage.slide4Sub'),
      cta: t('landing.homepage.slide4Cta'),
      ctaLink: '/register',
      mockup: '/images/mockup-dashboard-desktop.png',
      mockupType: 'laptop',
      accent: '#8B5CF6',
      overlay: null,
    },
    {
      id: 5,
      title: t('landing.homepage.slide5Title'),
      subtitle: t('landing.homepage.slide5Sub'),
      cta: t('landing.homepage.slide5Cta'),
      ctaLink: '/register',
      subCta: t('landing.homepage.slide5SubCta'),
      mockup: '/images/mockup-dashboard-mobile.png',
      mockupType: 'phone',
      accent: '#EF4444',
      overlay: 'sent',
    },
  ];

  const total = slides.length;

  const goTo = useCallback((idx) => {
    const next = ((idx % total) + total) % total;
    setOverlayReady(false);
    setCurrent(next);
  }, [total]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    const timer = setTimeout(() => setOverlayReady(true), 750);
    return () => clearTimeout(timer);
  }, [current]);

  useEffect(() => {
    timerRef.current = setInterval(next, 6500);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const pause = () => clearInterval(timerRef.current);
  const resume = () => { clearInterval(timerRef.current); timerRef.current = setInterval(next, 6500); };

  const onTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    isSwiping.current = false;
  };

  const onTouchMove = (e) => {
    if (isSwiping.current) return;
    const dx = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const dy = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    if (dy > 10 && dy > dx) {
      isSwiping.current = false;
    }
  };

  const onTouchEnd = (e) => {
    const dx = touchStartRef.current.x - e.changedTouches[0].clientX;
    const dy = Math.abs(touchStartRef.current.y - e.changedTouches[0].clientY);
    if (Math.abs(dx) > 50 && Math.abs(dx) > dy * 1.5) {
      dx > 0 ? next() : prev();
    }
  };

  return (
    <section
      className="relative select-none bg-[#0B1F3B]"
      style={{ touchAction: 'pan-y' }}
      data-testid="homepage-slider"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <SliderStyles />

      <div className="relative overflow-hidden min-h-[660px] sm:min-h-[450px] md:min-h-[560px] lg:min-h-[660px]">
        {slides.map((slide, index) => {
          const isActive = index === current;
          const showOverlay = isActive && overlayReady;
          return (
            <div
              key={slide.id}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{
                opacity: isActive ? 1 : 0,
                pointerEvents: isActive ? 'auto' : 'none',
                visibility: isActive ? 'visible' : 'hidden',
                transitionProperty: 'opacity',
                transitionDelay: isActive ? '0ms' : '700ms',
              }}
              data-testid={`slide-${index}`}
            >
              <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-[0.07] blur-[120px]" style={{ backgroundColor: slide.accent }} />
              <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

              <div className="relative z-10 container mx-auto px-5 sm:px-8 lg:px-12 h-full">
                <div className="min-h-[660px] sm:min-h-[450px] md:min-h-[560px] lg:min-h-[660px] flex items-center py-6 sm:py-8 md:py-10 lg:py-12">
                  <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between w-full gap-5 sm:gap-6 lg:gap-12">
                    <div className="max-w-[520px] w-full sm:w-auto">
                      {slide.eyebrow && (
                        <p className="text-sm sm:text-base text-blue-400 font-medium mb-3 tracking-wide" data-testid={`slider-eyebrow-${index}`}>
                          {slide.eyebrow}
                        </p>
                      )}
                      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.15] mb-3 sm:mb-5 whitespace-pre-line" data-testid={`slider-title-${index}`}>
                        {slide.title}
                      </h2>
                      <p className="text-sm sm:text-base md:text-lg text-slate-300 mb-5 sm:mb-8 leading-relaxed max-w-[440px]" data-testid={`slider-subtitle-${index}`}>
                        {slide.subtitle}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        {slide.ctaIsAnchor ? (
                          <a href={slide.ctaLink}>
                            <Button size="lg" className="text-sm sm:text-base px-6 sm:px-8 py-3 h-auto rounded-full font-semibold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 transition-all hover:scale-[1.03] active:scale-[0.98]" data-testid={`slider-cta-primary-${index}`} data-track-id={`slider-cta-primary-${index}`} data-track-label={`Slider CTA ${index + 1}`} data-track-category="cta">
                              {slide.cta}
                              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                            </Button>
                          </a>
                        ) : (
                          <Link to={slide.ctaLink}>
                            <Button size="lg" className="text-sm sm:text-base px-6 sm:px-8 py-3 h-auto rounded-full font-semibold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 transition-all hover:scale-[1.03] active:scale-[0.98]" data-testid={`slider-cta-primary-${index}`} data-track-id={`slider-cta-primary-${index}`} data-track-label={`Slider CTA ${index + 1}`} data-track-category="cta">
                              {slide.cta}
                              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                            </Button>
                          </Link>
                        )}
                        <Link to="/demo">
                          <Button variant="outline" size="lg" className="text-sm sm:text-base px-6 sm:px-8 py-3 h-auto rounded-full font-semibold border-white/20 text-white bg-white/5 hover:bg-white/10 transition-all" data-testid={`slider-cta-secondary-${index}`} data-track-id={`slider-cta-demo-${index}`} data-track-label="Bekijk demo" data-track-category="cta">
                            {t('landing.homepage.demoBtn')}
                          </Button>
                        </Link>
                      </div>
                      {slide.subCta && (
                        <p className="mt-3 text-sm text-slate-400/70">{slide.subCta}</p>
                      )}
                    </div>

                    <div className="flex items-center justify-center flex-shrink-0">
                      {slide.mockupType === 'phone'
                        ? <PhoneMockup src={slide.mockup} overlay={slide.overlay} showOverlay={showOverlay} t={t} />
                        : <LaptopMockup src={slide.mockup} />
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <button onClick={prev} className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-20 hidden md:flex w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 items-center justify-center text-white/70 hover:bg-white/25 hover:text-white transition-all" aria-label={t('landing.hc_vorige')} data-testid="slider-prev"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={next} className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-20 hidden md:flex w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 items-center justify-center text-white/70 hover:bg-white/25 hover:text-white transition-all" aria-label={t('landing.hc_volgende')} data-testid="slider-next"><ChevronRight className="w-5 h-5" /></button>
      </div>

      <div className="relative z-10 flex items-center justify-center gap-2 pt-5 sm:pt-6 md:pt-7 pb-3 bg-[#0B1F3B]" data-testid="slider-dots">
        {slides.map((s, i) => (
          <button key={s.id} onClick={() => goTo(i)} className="group p-1" aria-label={`Slide ${i + 1}`} data-testid={`slider-dot-${i}`}>
            <div className={`h-2 rounded-full transition-all duration-500 ${i === current ? 'w-8 bg-blue-500' : 'w-2 bg-white/30 group-hover:bg-white/60'}`} />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HomepageSlider;
